import $ from 'jquery';
import ModalView from 'views/ModalView';

const SELECTORS = {
    EXTEND: '.js-timeout-extend',
    COUNTDOWN: '.js-timeout-countdown',
};

const CONFIG = {
    TIMEOUT_SECONDS: '300', // 300 seconds = 5 minutes
    EXTEND_WINDOW_SECONDS: '20',
    LOGOUT_URL: '/login?reason=inactivity',
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class TimeoutView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;
        this.isActive = false;

        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {TimeoutView}
     */
    setupHandlers() {
        this.handleExtendClick = this.onExtendClick.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {TimeoutView}
     */
    createChildren() {
        this.timeoutLength = CONFIG.TIMEOUT_SECONDS - CONFIG.EXTEND_WINDOW_SECONDS;
        this.$extend = this.$element.find('.js-timeout-extend');
        this.$countdown = this.$element.find('.js-timeout-countdown');

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {TimeoutView}
     */
    layout() {
        this.isActive = false;

        setTimeout(() => {
            this._modal = new ModalView(this.$element);
            this.isActive = true;
            this.countdown(CONFIG.EXTEND_WINDOW_SECONDS);
        }, this.secondsToMiliseconds(this.timeoutLength));

        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {TimeoutView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$extend.length; i++) {
            this.$extend[i].addEventListener('click', this.handleExtendClick);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {TimeoutView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$extend.length; i++) {
            this.$extend[i].removeEventListener('click', this.handleExtendClick);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {TimeoutView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Handles extend time click event
     *
     * @returns {TimeoutView}
     */
    onExtendClick() {
        this.render();
        return this;
    }

    /**
     * Visual display of seconds remaining to extend timeout
     *
     * @param {number} seconds
     */
    countdown(seconds) {
        this.$countdown.text(seconds);
        setTimeout(() => {
            if ((seconds > 0) && this.isActive) {
                this.countdown(seconds - 1);
            } else if (seconds <= 0) {
                this.logOut();
            }
        }, 1000);
    }

    /**
     * Send user to logout URL
     */
    logOut() {
        window.location = CONFIG.LOGOUT_URL;
    }

    /**
     * Convert seconds to ms
     *
     * @param num
     * @returns {number}
     */
    secondsToMiliseconds(num) {
        return num * 1000;
    }
}
